import React, { useState } from "react";
import { usePdfCanvas } from "../hooks/usePdfCanvas";

interface LinkedInPostPreviewProps {
  postText:       string;
  pdfBase64:      string | null;
  pptxBase64?:    string | null;
  totalSlides:    number;
  documentTitle?: string;
  /** When true, renders the card inline (no modal overlay, no action buttons). */
  standalone?:    boolean;
  onPost?:        () => void;
  onClose?:       () => void;
  userName?:      string;

  // Admin-only: LinkedIn direct publish
  isAdmin?:            boolean;
  linkedinConnected?:  boolean;
  linkedinPublishing?: boolean;
  linkedinError?:      string | null;
  onConnectLinkedIn?:  () => void;
  onPublishLinkedIn?:  () => void;
}

const LinkedInPostPreview: React.FC<LinkedInPostPreviewProps> = ({
  postText,
  pdfBase64,
  pptxBase64,
  totalSlides,
  documentTitle,
  standalone = false,
  onPost,
  onClose,
  userName = "You",
  isAdmin = false,
  linkedinConnected = false,
  linkedinPublishing = false,
  linkedinError = null,
  onConnectLinkedIn,
  onPublishLinkedIn,
}) => {
  const [copied,       setCopied]       = useState(false);
  const [expanded,     setExpanded]     = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const { canvasRef, numPages } = usePdfCanvas(pdfBase64, currentSlide);

  // numPages comes from the loaded pdfjs document — always accurate once the PDF is ready.
  // totalSlides (prop) is only a hint; numPages wins whenever the PDF is loaded.
  const effectiveSlides = numPages > 0 ? numPages : totalSlides;

  const MAX_PREVIEW_LEN = 300;
  const isLong      = postText.length > MAX_PREVIEW_LEN;
  const displayText = isLong && !expanded ? postText.slice(0, MAX_PREVIEW_LEN) : postText;
  // Show the canvas whenever we have a PDF.
  const showSlides  = !!pdfBase64;

  const handleCopy = () => {
    navigator.clipboard.writeText(postText).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const prev = () => setCurrentSlide(s => Math.max(0, s - 1));
  const next = () => setCurrentSlide(s => Math.min(effectiveSlides - 1, s + 1));

  const slug = (documentTitle ?? "carousel")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const cardChrome = (
    <div style={{ border: "1px solid #dce6f0", borderRadius: "10px", overflow: "hidden", position: "relative", background: "#fff" }}>

      {/* Copy icon — top-right corner */}
      <button
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy post"}
        aria-label="Copy post text"
        style={{
          position: "absolute", top: "10px", right: "10px", zIndex: 1,
          background: "none", border: "none", cursor: "pointer",
          padding: "4px", display: "flex", alignItems: "center",
          color: copied ? "#16a34a" : "#aaa",
          transition: "color 0.2s",
        }}
      >
        {copied ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2"/></svg>
        )}
      </button>

      {/* Profile row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px 8px" }}>
        <div style={{
          width: "42px", height: "42px", borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg, #5B4FE8, #8B80FF)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: "15px",
        }}>
          {userName.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: "#000", letterSpacing: "-0.01em" }}>{userName}</p>
          <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#666" }}>Now · 🌐</p>
        </div>
      </div>

      {/* Post text */}
      <div style={{ padding: "0 14px 10px" }}>
        <div style={{ fontSize: "14px", color: "#0A0A0A", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {displayText}
          {isLong && !expanded && (
            <>{"… "}<span style={{ color: "#0A66C2", cursor: "pointer", fontWeight: 600 }} onClick={e => { e.stopPropagation(); setExpanded(true); }}>more</span></>
          )}
        </div>
      </div>

      {/* Single-slide document viewer — only when PDF is available */}
      {showSlides && (
        <div style={{ position: "relative", background: "#f0f0f0", borderTop: "1px solid #dce6f0" }}>
          <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "auto" }} />
          {effectiveSlides > 1 && (
            <>
              <button className="li-nav-btn" style={{ left: "8px" }} onClick={e => { e.stopPropagation(); prev(); }} disabled={currentSlide === 0} aria-label="Previous slide">‹</button>
              <button className="li-nav-btn" style={{ right: "8px" }} onClick={e => { e.stopPropagation(); next(); }} disabled={currentSlide === effectiveSlides - 1} aria-label="Next slide">›</button>
              <div style={{
                position: "absolute", bottom: "8px", left: "50%", transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.5)", color: "#fff",
                fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "20px",
                pointerEvents: "none",
              }}>
                {currentSlide + 1} / {effectiveSlides}
              </div>
            </>
          )}
        </div>
      )}

      {/* Document title bar */}
      {documentTitle && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "8px 14px", borderTop: "1px solid #e9e9e9", background: "#f8f9fa",
        }}>
          <span style={{ fontSize: "15px", flexShrink: 0 }}>📄</span>
          <span style={{ flex: 1, fontSize: "13px", fontWeight: 600, color: "#0A0A0A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {documentTitle}
          </span>
        </div>
      )}

      {/* Decorative reaction row */}
      <div style={{ display: "flex", padding: "6px 14px", borderTop: "1px solid #e9e9e9" }}>
        {(["👍 Like", "💬 Comment", "🔁 Repost", "📤 Send"] as const).map(label => (
          <div key={label} style={{ flex: 1, textAlign: "center", fontSize: "11px", color: "#666", padding: "4px 0", userSelect: "none" }}>{label}</div>
        ))}
      </div>
    </div>
  );

  const css = `
    @keyframes liCardIn {
      from { opacity: 0; transform: translateY(24px) scale(.97); }
      to   { opacity: 1; transform: translateY(0)   scale(1);   }
    }
    .li-preview-card { animation: liCardIn 400ms cubic-bezier(.22,1,.36,1) both; }
    @media (prefers-reduced-motion: reduce) { .li-preview-card { animation: none; } }
    .li-nav-btn {
      position: absolute; top: 50%; transform: translateY(-50%);
      width: 36px; height: 36px; border-radius: 50%;
      background: rgba(0,0,0,0.45); border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-size: 20px; line-height: 1; z-index: 1;
      transition: background 0.15s;
    }
    .li-nav-btn:hover:not(:disabled) { background: rgba(0,0,0,0.7); }
    .li-nav-btn:disabled { opacity: 0.25; cursor: default; }
  `;

  // ── Standalone mode: plain inline card, no modal shell ───────────────────────
  if (standalone) {
    return (
      <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", width: "100%" }}>
        <style>{css}</style>
        {cardChrome}
        {(pdfBase64 || pptxBase64) && (
          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
            {pdfBase64 && (
              <button onClick={() => downloadB64(pdfBase64, `${slug}-carousel.pdf`, "application/pdf")} style={dlBtnStyle}>
                ↓ Download PDF
              </button>
            )}
            {pptxBase64 && (
              <button onClick={() => downloadB64(pptxBase64, `${slug}-carousel.pptx`, "application/vnd.openxmlformats-officedocument.presentationml.presentation")} style={dlBtnStyle}>
                ↓ Download PPT
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Modal mode: fixed overlay with header + action buttons ───────────────────
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        fontFamily: "'Bricolage Grotesque', sans-serif",
      }}
      onClick={onClose}
    >
      <style>{css}</style>
      <div
        className="li-preview-card"
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: "16px",
          maxWidth: "480px", width: "100%", maxHeight: "92vh",
          overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.24)",
        }}
      >
        {/* Modal header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: "1px solid #e9e9e9",
          position: "sticky", top: 0, background: "#fff", zIndex: 2,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LinkedInMark size={20} />
            <span style={{ fontWeight: 700, fontSize: "14px", color: "#0A0A0A", letterSpacing: "-0.01em" }}>Post preview</span>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "18px", lineHeight: 1, padding: "4px" }}>✕</button>
        </div>

        {/* Card chrome */}
        <div style={{ padding: "12px 16px 4px" }}>{cardChrome}</div>

        {/* Download row */}
        {(pdfBase64 || pptxBase64) && (
          <div style={{ padding: "10px 16px 4px", display: "flex", gap: "8px" }}>
            {pdfBase64 && (
              <button onClick={() => downloadB64(pdfBase64, `${slug}-carousel.pdf`, "application/pdf")} style={dlBtnStyle}>↓ PDF</button>
            )}
            {pptxBase64 && (
              <button onClick={() => downloadB64(pptxBase64, `${slug}-carousel.pptx`, "application/vnd.openxmlformats-officedocument.presentationml.presentation")} style={dlBtnStyle}>↓ PPT</button>
            )}
          </div>
        )}

        {/* Post button */}
        <div style={{ padding: "8px 16px 18px" }}>
          <button
            onClick={onPost}
            style={{
              width: "100%", padding: "14px",
              background: "#0A66C2", color: "#fff",
              border: "none", borderRadius: "24px",
              fontSize: "15px", fontWeight: 700, letterSpacing: "-0.01em",
              cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            <LinkedInMark size={16} inverted />
            Post on LinkedIn
          </button>

          {/* Admin-only: connect or publish directly */}
          {isAdmin && (
            <div style={{ marginTop: "10px" }}>
              {!linkedinConnected ? (
                <button
                  onClick={onConnectLinkedIn}
                  style={{
                    width: "100%", padding: "12px",
                    background: "#fff", color: "#0A66C2",
                    border: "2px solid #0A66C2", borderRadius: "24px",
                    fontSize: "14px", fontWeight: 700, letterSpacing: "-0.01em",
                    cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  }}
                >
                  <LinkedInMark size={15} />
                  Connect LinkedIn to publish directly
                </button>
              ) : (
                <button
                  onClick={onPublishLinkedIn}
                  disabled={linkedinPublishing}
                  style={{
                    width: "100%", padding: "12px",
                    background: linkedinPublishing ? "#e8f0fb" : "#004182", color: "#fff",
                    border: "none", borderRadius: "24px",
                    fontSize: "14px", fontWeight: 700, letterSpacing: "-0.01em",
                    cursor: linkedinPublishing ? "not-allowed" : "pointer",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    opacity: linkedinPublishing ? 0.7 : 1,
                  }}
                >
                  <LinkedInMark size={15} inverted />
                  {linkedinPublishing ? "Publishing…" : "Publish directly to LinkedIn"}
                </button>
              )}
              {linkedinError && (
                <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#dc2626", textAlign: "center" }}>
                  {linkedinError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const dlBtnStyle: React.CSSProperties = {
  flex: 1, padding: "10px",
  background: "#f4f4f4", border: "1px solid #e0e0e0",
  borderRadius: "10px", fontSize: "13px", fontWeight: 600,
  cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif",
  color: "#0A0A0A",
};

const LinkedInMark: React.FC<{ size?: number; inverted?: boolean }> = ({ size = 20, inverted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <rect width="24" height="24" rx="4" fill={inverted ? "rgba(255,255,255,0.2)" : "#0A66C2"} />
    <path d="M7 10H5v8h2v-8zM6 9a1.25 1.25 0 100-2.5A1.25 1.25 0 006 9zM19 14c0-2.2-1.5-4-3.5-4a3.5 3.5 0 00-2.5 1.1V10H11v8h2v-4c0-1.1.9-2 2-2s2 .9 2 2v4h2v-4z" fill="#fff" />
  </svg>
);

function downloadB64(b64: string, filename: string, mime: string) {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const blob  = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url   = URL.createObjectURL(blob);
  const a     = document.createElement("a");
  a.href = url; a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default LinkedInPostPreview;
